import React from 'react';
import type { AIAgentResponse } from '@troia-v3/types';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { StatusPill, Switch } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { AgentChannelIcons, AgentScore, agentChannelIds } from './agent-visuals';
import type { AgentChannelInfo, AgentWithScore } from './agent-visuals';

/**
 * Tipos de ação mantidos por retrocompatibilidade do barrel (`../components`).
 */
export type AgentCardAction = 'edit' | 'delete' | 'toggleStatus' | 'duplicate';

/**
 * Props do AgentCard (view "Cards"). Padrão-lista travado (referência Usuários):
 * menu ••• de ações + switch inline de status (Switch shared sm — F47). Sem
 * checkbox de seleção em cards (decisão F34 — seleção só em lista/tabela).
 * Avatar sem foto = slate via AvatarFallback.
 */
export interface AgentCardProps {
  agent: AIAgentResponse;
  /** id → canal (nome/providerId) pra renderizar os círculos `.ag-chs` do mockup */
  channelsById?: Map<string, AgentChannelInfo>;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onToggleStatus?: (id: string, currentStatus: string) => void;
  onDuplicate?: (id: string) => void;
}

const getInitials = (name: string) => {
  const parts = name.split(/[\s—–-]+/).filter(Boolean);
  const first = parts[0] ?? '';
  const second = parts[1] ?? '';
  if (first && second) return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase();
  return name.trim().slice(0, 2).toUpperCase();
};

export const AgentCard: React.FC<AgentCardProps> = ({
  agent,
  channelsById,
  onEdit,
  onDelete,
  onToggleStatus,
  onDuplicate,
}) => {
  const isActive = agent.status === 'active';
  const qualityScore = (agent as AgentWithScore).qualityScore;
  const channelIds = agentChannelIds(agent);

  const actions: CardAction[] = [];
  if (onEdit) actions.push({ label: 'Editar', icon: 'edit', onClick: () => onEdit(agent.id) });
  if (onToggleStatus) {
    actions.push({
      label: isActive ? 'Pausar' : 'Ativar',
      icon: 'power',
      onClick: () => onToggleStatus(agent.id, agent.status),
    });
  }
  if (onDuplicate) actions.push({ label: 'Duplicar', icon: 'copy', onClick: () => onDuplicate(agent.id) });
  if (onDelete) actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(agent.id), variant: 'danger', dividerBefore: true });

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
        cursor: onEdit ? 'pointer' : 'default',
      }}
      onClick={() => onEdit?.(agent.id)}
      data-testid={`agent-card-${agent.id}`}
    >
      <div className="flex items-start gap-3">
        <Avatar className="w-11 h-11 flex-shrink-0" isSquircle>
          <AvatarFallback isSquircle>
            <span className="text-white font-semibold text-sm">{getInitials(agent.name)}</span>
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
            {agent.name}
          </h3>
          <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
            {agent.description || 'Sem descrição'}
          </p>
        </div>

        {actions.length > 0 && (
          <div className="flex-shrink-0 -mr-1 -mt-1" onClick={(e) => e.stopPropagation()}>
            <CardActionsMenu actions={actions} size="sm" data-testid={`agent-actions-${agent.id}`} />
          </div>
        )}
      </div>

      {/* `.meta` do mockup: chip do modelo + círculos de canais */}
      <div className="mt-3 flex items-center flex-wrap gap-2">
        <span
          className="inline-flex items-center gap-1.5 h-[23px] px-2.5 rounded-full text-xs font-medium"
          style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-card-text)' }}
        >
          <Icon name="cpu" className="w-3 h-3" style={{ color: 'var(--theme-muted-text)' }} />
          {agent.model}
        </span>
        {channelsById && <AgentChannelIcons channelIds={channelIds} channelsById={channelsById} />}
      </div>

      {/* Footer: switch de status (F47) + interações */}
      <div
        className="mt-3 pt-3 flex items-center gap-2"
        style={{ borderTop: '1px solid var(--theme-card-border)' }}
      >
        {onToggleStatus ? (
          <div
            className="inline-flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
            data-testid={`agent-toggle-${agent.id}`}
          >
            <Switch
              size="sm"
              checked={isActive}
              onChange={() => onToggleStatus(agent.id, agent.status)}
            />
            <span className="text-xs font-semibold" style={{ color: isActive ? 'var(--theme-success-fg)' : 'var(--theme-muted-text)' }}>
              {isActive ? 'Ativo' : 'Pausado'}
            </span>
          </div>
        ) : (
          <StatusPill
            variant={isActive ? 'success' : 'neutral'}
            label={isActive ? 'Ativo' : 'Pausado'}
            data-testid={`agent-status-${agent.id}`}
          />
        )}

        <span className="flex-1" />

        {/* `.ag-score` do mockup — slot direito do rodapé; sem score = sem barra */}
        {qualityScore !== undefined ? (
          <AgentScore score={qualityScore} />
        ) : (
          agent.totalInteractions !== undefined && (
            <span
              className="inline-flex items-center gap-1.5 text-xs font-medium"
              title="Interações — o score de qualidade aparece após as primeiras avaliações"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              <Icon name="activity" className="w-3.5 h-3.5" />
              {agent.totalInteractions.toLocaleString('pt-BR')}
            </span>
          )
        )}
      </div>
    </div>
  );
};

AgentCard.displayName = 'AgentCard';
